import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import {
  api,
  getTrustedNetworks as fetchTrustedNetworks,
} from "../Assets/api/api";
import { toast } from "react-toastify";

const TrustedNetwork = createContext();

export const useTrustedNetwork = () => {
  return useContext(TrustedNetwork);
};

export const TrustedNetworkContext = (props) => {
  const { children, user } = props;
  const [isLoadingSchools, setIsLoadingSchools] = useState(false);
  const [schools, setSchools] = useState([]);
  const [receivedRequests, setReceivedRequests] = useState([]);
  const [createdRequests, setCreatedRequests] = useState([]);
  const [isLoadingRequests, setIsLoadingRequests] = useState(false);
  const [isApprovingTrustRequest, setIsApprovingTrustRequest] = useState({});
  const [isRejectingTrustRequest, setIsRejectingTrustRequest] = useState({});
  // Trusted networks state
  const [trustedNetworks, setTrustedNetworks] = useState([]);
  const [isLoadingTrustedNetworks, setIsLoadingTrustedNetworks] =
    useState(false);
  const [isEndingTrustedNetwork, setIsEndingTrustedNetwork] = useState({});
  const [isUpdatingTrustedNetwork, setIsUpdatingTrustedNetwork] = useState({});
  const [isUpdatingGroupNetwork, setIsUpdatingGroupNetwork] = useState({});
  const [isTrusted, setIsTrusted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [schoolData, setSchoolData] = useState(null);

  useEffect(() => {
    const fetchSchoolTrustStatus = async () => {
      if (!user?.slug || !user?.accessToken) {
        setIsInitialLoading(false);
        return;
      }

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_EP_URL}/api/school/get-school-info${user.slug}`,
          {
            headers: {
              Authorization: "Bearer " + user.accessToken,
            },
          }
        );

        const schoolData = response.data?.result[0];
        setSchoolData(schoolData);
        setIsTrusted(!!schoolData?.isTrusted);
      } catch (err) {
        console.error("Failed to fetch school trust status", err);
        setIsTrusted(false);
      } finally {
        setIsInitialLoading(false);
      }
    };

    fetchSchoolTrustStatus();
  }, [user?.slug, user?.accessToken]);

  function extractSchoolId(query) {
    const match = query.match(/=(.*)/);
    return match ? match[1] : null;
  }

  const getAllSchools = async () => {
    try {
      setIsLoadingSchools(true);
      const response = await api.get(
        `/api/trust-network/get-all-schools${user.slug}`
      );
      setIsLoadingSchools(false);
      setSchools(response.data.result);
    } catch (error) {
      setIsLoadingSchools(false);
    }
  };

  const generateTrustRequest = async (targetSchoolId) => {
    try {
      await api.post(`/api/trust-request${user.slug}`, {
        requesterSchoolId: extractSchoolId(user.slug),
        targetSchoolId,
      });
      toast.success("Request generated successfully!");
      let clonedSchools = [...schools];
      const index = clonedSchools.findIndex(
        (school) => school.OwnStgUrl === targetSchoolId
      );
      clonedSchools[index].requested = true;
      setSchools(clonedSchools);
      // Refresh the requests to show the newly created one
      getTrustRequests();
    } catch (error) {
      toast.error("Request generating failed!");
    }
  };

  const getTrustRequests = async () => {
    try {
      setIsLoadingRequests(true);
      const response = await api.get(`/api/trust-request${user.slug}`);

      setReceivedRequests(response.data.result?.received || []);
      setCreatedRequests(response.data.result?.sent || []);
      setIsLoadingRequests(false);
    } catch (error) {
      setIsLoadingRequests(false);
      setReceivedRequests([]);
      setCreatedRequests([]);
    }
  };

  const cancelTrustRequests = async (requestId) => {
    try {
      setIsLoadingRequests(true);
      await api.delete(`/api/trust-request/${requestId}${user.slug}`);

      setIsLoadingRequests(false);
    } catch (error) {
      setIsLoadingRequests(false);
    }
  };

  const getTrustedNetworks = async () => {
    try {
      setIsLoadingTrustedNetworks(true);
      const response = await api.get(`/api/trust-network${user.slug}`);
      setTrustedNetworks(response.data.result);
      setIsLoadingTrustedNetworks(false);
    } catch (error) {
      setIsLoadingTrustedNetworks(false);
      toast.error("Failed to fetch trusted networks!");
    }
  };

  const approveTrustRequest = async (requestId, config) => {
    setIsApprovingTrustRequest((prev) => ({ ...prev, [requestId]: true }));
    try {
      await api.put(`/api/trust-request/${requestId}${user.slug}`, {
        status: "approved",
        trustNetworkConfig: config,
      });
      toast.success("Trust request approved successfully!");
      getTrustRequests(); // Refresh the requests after approval
      return { success: true };
    } catch (error) {
      toast.error("Request accepting failed!");
      return { success: false, error };
    } finally {
      setIsApprovingTrustRequest((prev) => ({ ...prev, [requestId]: false }));
    }
  };

  const rejectTrustRequest = async (requestId) => {
    setIsRejectingTrustRequest((prev) => ({ ...prev, [requestId]: true }));
    try {
      await api.put(`/api/trust-request/${requestId}${user.slug}`, {
        status: "rejected",
      });
      toast.success("Trust request rejected successfully!");
      getTrustRequests(); // Refresh the requests after rejection
      return { success: true };
    } catch (error) {
      toast.error("Request rejecting failed!");
      return { success: false, error };
    } finally {
      setIsRejectingTrustRequest((prev) => ({ ...prev, [requestId]: false }));
    }
  };

  const endTrustedNetwork = async (networkId) => {
    setIsEndingTrustedNetwork((prev) => ({ ...prev, [networkId]: true }));
    try {
      await api.delete(`/api/trust-network/${networkId}${user.slug}`);
      getTrustedNetworks(); // Refresh the networks list
      toast.success("Trusted network connection ended successfully");
    } catch (error) {
      toast.error("Failed to end trusted network connection");
    } finally {
      setIsEndingTrustedNetwork((prev) => ({ ...prev, [networkId]: false }));
    }
  };

  const updateTrustedNetworkConfig = async (networkId, config) => {
    setIsUpdatingTrustedNetwork((prev) => ({ ...prev, [networkId]: true }));
    try {
      await api.put(`/api/trust-network-config/${networkId}${user.slug}`, {
        ...config,
      });
      getTrustedNetworks(); // Refresh the networks list
      toast.success("Trusted network configuration updated successfully");
    } catch (error) {
      toast.error("Failed to update trusted network configuration");
    } finally {
      setIsUpdatingTrustedNetwork((prev) => ({ ...prev, [networkId]: false }));
    }
  };

  const updateGroupNetworkConfig = async (groupNetworkId, config) => {
    setIsUpdatingGroupNetwork((prev) => ({ ...prev, [groupNetworkId]: true }));
    try {
      await api.patch(
        `/api/group-network/${groupNetworkId}/config${user.slug}`,
        {
          ...config,
        }
      );
      toast.success("Group network configuration updated successfully");
    } catch (error) {
      toast.error("Failed to update group network configuration");
    } finally {
      setIsUpdatingGroupNetwork((prev) => ({
        ...prev,
        [groupNetworkId]: false,
      }));
    }
  };

  const updateIsTrusted = async () => {
    if (!user?.slug || !user?.accessToken) return;

    setIsLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_EP_URL}/api/school/update-trusted-everyone${user.slug}`,
        {},
        {
          headers: {
            Authorization: "Bearer " + user.accessToken,
          },
        }
      );

      const trusted = !!response.data.result.isTrusted;
      setIsTrusted(trusted);
      return trusted;
    } catch (error) {
      console.error("Failed to update trusted status", error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <TrustedNetwork.Provider
      value={{
        getAllSchools,
        isLoadingSchools,
        schools,
        generateTrustRequest,
        getTrustRequests,
        cancelTrustRequests,
        isLoadingRequests,
        isApprovingTrustRequest,
        isRejectingTrustRequest,
        approveTrustRequest,
        rejectTrustRequest,
        receivedRequests,
        createdRequests,
        // Trusted networks
        getTrustedNetworks,
        isLoadingTrustedNetworks,
        trustedNetworks,
        endTrustedNetwork,
        isEndingTrustedNetwork,
        updateTrustedNetworkConfig,
        isUpdatingTrustedNetwork,
        user,
        //group network
        updateGroupNetworkConfig,
        isUpdatingGroupNetwork,
        isTrusted,
        updateIsTrusted,
        isLoading,
        isInitialLoading,
        schoolData,
      }}
    >
      {children}
    </TrustedNetwork.Provider>
  );
};
